import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import ACTIVATE_LICENSE from '../../../queries/changeLicenseQueries/activateLicenseMutation';
import ApolloError from '@shared/components/ApolloError';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import { DialogActions, DialogContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { ActivateLicenseValidator } from './validators/activateLicenseValidator';

const FORM_ID = 'change-license';

const ACTIVATE_LICENSE_MESSAGE = 'User has been Activated.';
const ACTIVATE_CONFIRMATION_MESSAGE = 'Are you sure you want to reactivate this licensed user?';
const ACTIVATE_TEXT = 'Activate';
const CANCEL_TEXT = 'Cancel';
const SAVE_TEXT = 'Save';

/**
 * @typedef {object} ActivateLicenseProps
 * @property {React.ReactNode} [dialogContent]
 * @property {React.ReactNode} [dialogActions]
 * @property {number} [externalUserId]
 * @property {string} [previousLink]
 */

/**
 * @param {ActivateLicenseProps} props
 */
const ActivateLicense = props => {
  const { dialogContent, dialogActions, externalUserId, previousLink } = props;
  const navigate = useNavigate();
  const client = useApolloClient();

  const form = useForm({
    resolver: joiResolver(ActivateLicenseValidator.schema),
    defaultValues: {
      externalUserId
    }
  });
  const { handleSubmit } = form;

  const [activateLicense, { error: activateLicenseError }] = useMutation(ACTIVATE_LICENSE, {
    update: () => client.clearStore()
  });

  const handleConfirm = async formData => {
    const result = await activateLicense({
      variables: { externalUserId: formData.externalUserId }
    });

    if (!result.data.activateUserLicense.Success) {
      showErrorToast(result.data.activateUserLicense.Errors.map(e => e.Message).join(', '));
      return;
    }

    showSuccessToast(ACTIVATE_LICENSE_MESSAGE);
    navigate(previousLink);
  };

  if (activateLicenseError) {
    return <ApolloError />;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(handleConfirm)}>
      <DialogContent>{dialogContent}</DialogContent>

      <DialogActions>
        {dialogActions}

        <ConfirmDialogWithButton
          openModalBtnText={SAVE_TEXT}
          confirmBtnText={ACTIVATE_TEXT}
          cancelBtnText={CANCEL_TEXT}
          message={ACTIVATE_CONFIRMATION_MESSAGE}
          dialogMode={DIALOG_MODES.EXTERNAL_FORM_SUBMIT}
          formId={FORM_ID}
        />
      </DialogActions>
    </form>
  );
};

export default ActivateLicense;
